import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { 
  obtenerTodosLosCasos, 
  crearCaso, 
  actualizarCaso,
  actualizarEstadoCaso
} from '../../services/casoService';
import { crearVerificacion } from '../../services/verificacionService';
import { encryptId } from '../../utils/encrypt';
import Modal from '../../components/common/Modal';
import CasoForm from '../../components/common/casoForm';
import NotificationModal from '../../components/common/NotificationModal';
import ConfirmActionModal from '../../components/common/ConfirmActionModal';
import BackButton from '../../components/common/BackButton';
import AdminLayout from '../../components/layout/AdminLayout';
import './CasosAdmin.css';

const CasosAdmin = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [casos, setCasos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCaso, setSelectedCaso] = useState(null);
  const [filter, setFilter] = useState('todos');
  const [notification, setNotification] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'success'
  });
  const [confirmEstado, setConfirmEstado] = useState({
    isOpen: false,
    casoId: null,
    nuevoEstado: null,
    estadoActual: null
  });

  const showNotification = (title, message, type = 'success') => {
    setNotification({ isOpen: true, title, message, type });
  };

  const closeNotification = () => {
    setNotification({ ...notification, isOpen: false });
  };

  const cargarCasos = useCallback(async () => {
    try {
      setLoading(true);
      const data = await obtenerTodosLosCasos();
      setCasos(data);
    } catch (error) {
      showNotification('Error', 'Error al cargar casos: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    cargarCasos();
  }, [user, navigate, cargarCasos]);

  const handleGuardarCaso = async (casoData) => {
    try {
      if (selectedCaso?.id) {
        await actualizarCaso(selectedCaso.id, casoData);
        showNotification('Caso Actualizado', 'El caso ha sido actualizado exitosamente', 'success');
      } else {
        const nuevoCaso = await crearCaso(casoData);
        await crearVerificacion(nuevoCaso.id);
        showNotification('Caso Creado', 'El caso ha sido creado exitosamente', 'success');
      }
      setModalOpen(false);
      cargarCasos();
    } catch (error) {
      showNotification('Error', 'Error al guardar: ' + error.message, 'error');
    }
  };

  const handleCambiarEstadoClick = (casoId, nuevoEstado, estadoActual) => {
    setConfirmEstado({
      isOpen: true,
      casoId: casoId,
      nuevoEstado: nuevoEstado,
      estadoActual: estadoActual
    });
  };

  const handleConfirmCambioEstado = async () => {
    const textos = {
      nuevo: 'Nuevo',
      en_verificacion: 'En Verificacion',
      en_proceso: 'En Proceso',
      completado: 'Completado',
      cancelado: 'Cancelado'
    };
    try {
      await actualizarEstadoCaso(confirmEstado.casoId, confirmEstado.nuevoEstado);
      showNotification('Estado Actualizado', `El caso ha cambiado de estado a "${textos[confirmEstado.nuevoEstado]}"`, 'success');
      setConfirmEstado({ isOpen: false, casoId: null, nuevoEstado: null, estadoActual: null });
      cargarCasos();
    } catch (error) {
      showNotification('Error', 'Error al cambiar estado: ' + error.message, 'error');
      setConfirmEstado({ isOpen: false, casoId: null, nuevoEstado: null, estadoActual: null });
    }
  };

  const handleCancelCambioEstado = () => {
    setConfirmEstado({ isOpen: false, casoId: null, nuevoEstado: null, estadoActual: null });
  };

  const getEstadoBadge = (estado) => {
    const clases = {
      nuevo: 'badge-nuevo',
      en_verificacion: 'badge-verificacion',
      en_proceso: 'badge-proceso',
      completado: 'badge-completado',
      cancelado: 'badge-cancelado'
    };
    const textos = {
      nuevo: 'Nuevo',
      en_verificacion: 'En Verificacion',
      en_proceso: 'En Proceso',
      completado: 'Completado',
      cancelado: 'Cancelado'
    };
    return <span className={`estado-badge ${clases[estado]}`}>{textos[estado]}</span>;
  };

  const getEstadoTexto = (estado) => {
    const textos = {
      nuevo: 'Nuevo',
      en_verificacion: 'En Verificacion',
      en_proceso: 'En Proceso',
      completado: 'Completado',
      cancelado: 'Cancelado'
    };
    return textos[estado] || estado;
  };

  const casosFiltrados = filter === 'todos' ? casos : casos.filter(c => c.estado === filter);

  if (loading) return <div className="loading">Cargando casos...</div>;

  return (
    <AdminLayout>
      <div className="casos-admin-page">
        <div className="admin-navigation">
          <BackButton to="/dashboard" label="Volver al Dashboard" variant="outline" />
        </div>

        <div className="casos-header">
          <h1>Gestion de Casos Legales</h1>
          <button onClick={() => { setSelectedCaso(null); setModalOpen(true); }} className="btn-primary">
            + Nuevo Caso
          </button>
        </div>

        <div className="casos-filters">
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="todos">Todos los casos</option>
            <option value="nuevo">Nuevos</option>
            <option value="en_verificacion">En Verificacion</option>
            <option value="en_proceso">En Proceso</option>
            <option value="completado">Completados</option>
          </select>
        </div>

        <div className="casos-grid">
          {casosFiltrados.length === 0 ? (
            <p className="no-results">No hay casos para mostrar</p>
          ) : (
            casosFiltrados.map(caso => (
              <div key={caso.id} className="caso-card">
                <div className="caso-header">
                  <div className="caso-titulo">
                    <h3>{caso.vehiculo?.marca} {caso.vehiculo?.modelo}</h3>
                    <span className="caso-placa">{caso.vehiculo?.placa}</span>
                  </div>
                  {getEstadoBadge(caso.estado)}
                </div>
                <div className="caso-body">
                  <p><strong>Cliente ID:</strong> {caso.clienteId}</p>
                  <p><strong>Servicio:</strong> {caso.tipoServicio === 'compra' ? 'Asesoria en Compra' : 'Asesoria en Venta'}</p>
                  <p><strong>Fecha:</strong> {caso.fechaCreacion?.toLocaleDateString()}</p>
                </div>
                <div className="caso-footer">
                  <button 
                    className="btn-ver-detalle"
                    onClick={() => navigate(`/casos/${encryptId(caso.id)}`)}
                  >
                    Ver Detalle
                  </button>
                  <select 
                    className="estado-select"
                    value={caso.estado}
                    onChange={(e) => handleCambiarEstadoClick(caso.id, e.target.value, caso.estado)}
                  >
                    <option value="nuevo">Nuevo</option>
                    <option value="en_verificacion">En Verificacion</option>
                    <option value="en_proceso">En Proceso</option>
                    <option value="completado">Completado</option>
                  </select>
                </div>
              </div>
            ))
          )}
        </div>

        <Modal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          title={selectedCaso ? 'Editar Caso' : 'Nuevo Caso Legal'}
        >
          <CasoForm
            casoInicial={selectedCaso || {}}
            onSubmit={handleGuardarCaso}
            onCancel={() => setModalOpen(false)}
          />
        </Modal>

        <NotificationModal
          isOpen={notification.isOpen}
          onClose={closeNotification}
          title={notification.title}
          message={notification.message}
          type={notification.type}
          autoClose={true}
        />

        <ConfirmActionModal
          isOpen={confirmEstado.isOpen}
          onClose={handleCancelCambioEstado}
          onConfirm={handleConfirmCambioEstado}
          title="Confirmar cambio de estado"
          message={`¿Estas seguro de que deseas cambiar el estado de "${getEstadoTexto(confirmEstado.estadoActual)}" a "${getEstadoTexto(confirmEstado.nuevoEstado)}"?`}
          confirmText="Si, cambiar"
          cancelText="Cancelar"
        />
      </div>
    </AdminLayout>
  );
};

export default CasosAdmin;